import { useContext, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getRemainingExpiration,
  resetExpiration,
} from "../../../utils/getOTPExpirationTime";
import CityInfoStep from "./CityInfoStep";
import EmailStep from "./EmailStep";
import OTPStep from "./OTPStep";
import PasswordStep from "./PasswordStep";
import PhoneStep from "./PhoneStep";
import {
  RegisterFormContext,
  RegisterFormProvider,
} from "./RegisterFormContext";
import UserInforStep from "./UserInfoStep";
import AgentRegisterForm from "./AgentRegisterForm";

export default function RegisterWrapper() {
  const { user } = useSelector((state) => state.authData);
  if (user?.agent) {
    return <AgentRegisterForm />;
  }
  return (
    <RegisterFormProvider>
      <Register />
    </RegisterFormProvider>
  );
}

/**
 * The main entry point of the registeration process.
 * Can be accessed by either a guest user, or by an agent user.
 * In both cases, it first shows email form, then OTP to confirm the email, then a phone form, then a form for the user info. The final step is the main difference between both cases; for the normal user, the final step is the password step, whereas for the agent, it is a form for the user address.
 *
 * @returns {JSX.Element}
 */

const Register = () => {
  const { email, otp, phone, activeStep, setTimer } =
    useContext(RegisterFormContext);
  const user = useSelector((state) => state.authData.user);
  const inEmailStep = !email;
  const location = useLocation();
  const navigate = useNavigate();
  const prevEmailRef = useRef(email);
  const inOTPStep = !otp;

  // _________Rest OTP ON EMAIL CHANGE________//
  useEffect(() => {
    if (email && email !== prevEmailRef.current) {
      prevEmailRef.current = email;
      resetExpiration();
      setTimer(getRemainingExpiration());
    }
  }, [email]);

  useEffect(() => {
    if (email && !inOTPStep) {
      const handleBackButton = () => {
        // Clear location state without navigating away
        window.history.replaceState(null, "", location.pathname);
        navigate(location.pathname, { replace: true, state: {} });
      };

      window.addEventListener("popstate", handleBackButton);

      return () => {
        window.removeEventListener("popstate", handleBackButton);
      };
    }
  }, [navigate, location.pathname]);

  if (inEmailStep) {
    return <EmailStep />;
  }
  if (inOTPStep) {
    return <OTPStep />;
  }
  const inPhoneStep = !phone;
  if (inPhoneStep) {
    return <PhoneStep />;
  }
  const inInfoStep = activeStep === 1;
  if (inInfoStep) {
    return <UserInforStep />;
  }
  return user?.agent ? <CityInfoStep /> : <PasswordStep />;
};
